import client from './client';
import { API_BASE_URL } from './config';
import type { MessageAttachment } from '@hooks/useChat';

/**
 * Conversation attachments, for callers outside the chat composer.
 *
 * <p>An app run stages its inputs on the run's own conversation, so it uses the
 * same endpoints and the same 413/422 rejections. What differs is only that the
 * user can take a file back off before running, which chat has no need for.
 */
const attachmentsUrl = (conversationId: string): string =>
  `/api/v1/conversations/${encodeURIComponent(conversationId)}/attachments`;

const absoluteUrl = (url: string | null | undefined): string | null =>
  url ? `${API_BASE_URL}${url}` : null;

const messageFor = (error: unknown): string | null => {
  const response = (error as { response?: { status?: number; data?: { message?: unknown } } })
    .response;
  if (response?.status === 413) {
    return 'That file is too large. The maximum upload size is 500 MB.';
  }
  if (response?.status === 422) {
    const message = response.data?.message;
    return typeof message === 'string' && message.trim()
      ? message
      : 'That image could not be uploaded.';
  }
  return null;
};

export async function uploadAttachments(
  conversationId: string,
  files: readonly File[],
): Promise<MessageAttachment[]> {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  try {
    const { data } = await client.post<MessageAttachment[]>(
      attachmentsUrl(conversationId),
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data.map((attachment) => ({
      ...attachment,
      url: absoluteUrl(attachment.url),
    }));
  } catch (error) {
    const message = messageFor(error);
    throw message ? new Error(message) : error;
  }
}

/** Only ever succeeds for a file not yet sent — a sent one is part of the transcript. */
export async function deleteStagedAttachment(
  conversationId: string,
  attachmentId: string,
): Promise<void> {
  await client.delete(`${attachmentsUrl(conversationId)}/${encodeURIComponent(attachmentId)}`);
}
